import { defaultTheme } from '@helpscout/hsds-tokens'
import { getToken } from '@helpscout/hsds-tokens/getToken'
import { createGlobalStyle } from 'styled-components'

import { SMALL_TEXT_FONT_VARIANT_ALTERNATES } from 'hsds/utils/fonts'

import { HSDS_SCOPE } from './Provider'

export const BASE_FONT_SIZE = 13

const themes = { default: defaultTheme }

export type ThemeName = keyof typeof themes

const scopeSelector = `.${HSDS_SCOPE}`
const scopeSelectorHsApp = `.${HSDS_SCOPE}-hs-app`
const hsAppResetCSS = `
  /* reset element-level styling from hs-app */
  a,
  abbr,
  address,
  blockquote,
  body,
  button,
  code,
  dd,
  dl,
  dt,
  form,
  figure,
  fieldset,
  input,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  label,
  legend,
  li,
  ol,
  pre,
  table,
  textarea,
  ul {
    margin: initial;
    padding: initial;
    height: initial;
    width: initial;
    line-height: initial;
    border: 0;
    background-color: initial;
  }

  #sidebar-lt.reset {
    padding: initial !important;
  }

  .c-app-layout__sidebar.reset {
    width: auto !important;
  }
`

const globalHSDSTokens = `
  --hsds-global-font-family: ${getToken('font.family')};
  --hsds-global-font-family-system: ${getToken('font.system')};
  --hsds-global-font-family-mono: ${getToken('font.mono')};
  --hsds-global-font-size: ${getToken('font.size')};
  --hsds-global-background-color: ${getToken('color.background.surface')};
  --hsds-global-color: ${getToken('color.text.default')};
`

function getDataTheme(themeName: ThemeName) {
  if (!themes[themeName]) return ''
  return `
    ${themes[themeName].tokens};
  `
}

/**
 * Inject global HSDS tokens into the document.
 * Called at module load time to prevent FOUC, and also exported so the Provider
 * can call it during render as a safety net (e.g., iframe DOM resets in Storybook
 * can destroy the style tag while the JS module cache persists).
 *
 * Idempotent — deduplication is handled by the style tag's ID.
 */
export function injectGlobalTokens() {
  if (typeof document === 'undefined' || !document.head) return
  // JSDOM chokes on too many CSS variables
  if (
    typeof navigator !== 'undefined' &&
    navigator?.userAgent?.includes('jsdom')
  )
    return
  if (document.getElementById('hsds-design-tokens')) return

  const style = document.createElement('style')
  style.id = 'hsds-design-tokens'
  style.textContent = `
    ${hsAppResetCSS}

    :root {
      ${globalHSDSTokens};
      ${getDataTheme('default')};
    }

    body {
      background: var(--hsds-global-background-color);
      color: var(--hsds-global-color);
      /* !important makes HSDS the authoritative owner of the global body font,
         independent of hs-app-css (which may go away one day). It also wins over
         an embedded Beacon, whose old styled-components build injects a
         normal-weight body font-family rule. Importance does not inherit, so
         scoped HSDS surfaces still resolve the token from their scope container. */
      font-family: var(--hsds-global-font-family) !important;
      font-size: var(--hsds-global-font-size);
      font-variant-alternates: ${SMALL_TEXT_FONT_VARIANT_ALTERNATES};
    }

    /* Scoutie ss07: single-storey "g", on by default for UI text. Large HSDS
       headings opt out (Heading.styles). Scoped to Scoutie so a fallback
       font's own ss07 never kicks in. */
    @font-feature-values "Scoutie Sans" {
      @styleset {
        single-storey-g: 7;
      }
    }

    /* The UA font shorthand on form controls resets font-family (to Arial /
       the system font) and font-variant-alternates; inherit both. */
    input,
    textarea,
    select,
    button {
      font-family: inherit;
      font-variant-alternates: inherit;
    }

    /* Browser-default bold (700) is too heavy in Scoutie. */
    b,
    strong,
    th,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: ${getToken('font.weight.bold')};
    }
  `
  document.head.appendChild(style)
}

injectGlobalTokens()

// Pin the font tokens directly on the HSDS scope container (the ScopeProvider
// <div class="<scope>">). A declaration on the scope element beats an inherited
// value from :root/body, so each HSDS subtree owns its font regardless of what
// another HSDS instance in the same document (e.g. an embedded Beacon) writes to
// the global body/:root. This makes the token the single source of truth for the
// font within a scope, instead of relying on global body font-family.
const scopedFontTokens = `
  --hsds-global-font-family: ${getToken('font.family')};
  --hsds-global-font-family-system: ${getToken('font.system')};
  --hsds-global-font-family-mono: ${getToken('font.mono')};
  --hsds-global-font-size: ${getToken('font.size')};
  font-family: var(--hsds-global-font-family);
  font-size: var(--hsds-global-font-size);
  font-variant-alternates: ${SMALL_TEXT_FONT_VARIANT_ALTERNATES};
`

function getNonHsdsScopeStyles({
  themeName,
  scope,
}: {
  themeName: ThemeName
  scope: string
}) {
  return `
    ${scope} {
      ${globalHSDSTokens};
      ${getDataTheme(themeName)};
    }
  `
}

export default createGlobalStyle<{
  themeName: ThemeName
  scope: string
}>`
  ${({ themeName, scope }) => {
    const scopeSel = scope ? scope : scopeSelector
    const isCustomScope =
      scopeSel !== scopeSelector && scopeSel !== scopeSelectorHsApp

    return `
    ${scopeSel} {
      *, *::before, *::after {
        box-sizing: border-box;
      }
      ${scopedFontTokens}
    }

    ${
      isCustomScope ? getNonHsdsScopeStyles({ themeName, scope: scopeSel }) : ''
    }
    `
  }}
`
